/**
 * ポスト専用ピンレイヤー（design §12 / 要件 23-9, 23-10, 23-11）。
 *
 * - ポストは固定座標（投稿時の位置で確定）。参加者ピンのように移動しない。
 * - 参加者ピン（丸アバター DOM Marker）・会場ピン（symbol レイヤー）とは
 *   別クラス・別スタイル（角丸の写真アイコンバッジ）で区別する。
 * - クリックで本文＋画像＋投稿者の吹き出し（Popup）を表示。
 * - post_id 集合の差分で Marker を追加/削除するため、TTL 失効・削除された
 *   ポストは次の更新でピンが消える。
 */
import { Marker, Popup, type Map as MaplibreMap } from 'maplibre-gl';
import Supercluster from 'supercluster';
import type { Post } from '../api/client';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import './postLayer.css';

/** ポスト作成からの経過を「N分前 / N時間前 / N日前」で表す（言語連動）。 */
function relativeTime(createdAt: number, serverTime: number, lang: Lang): string {
  const tm = messages(lang).time;
  const sec = Math.max(0, serverTime - createdAt);
  const min = Math.floor(sec / 60);
  if (min < 1) return tm.justNow;
  if (min < 60) return tm.minutesAgo(min);
  const hour = Math.floor(min / 60);
  if (hour < 24) return tm.hoursAgo(hour);
  const day = Math.floor(hour / 24);
  return tm.daysAgo(day);
}

/** HTML 特殊文字をエスケープ（本文・表示名を吹き出しに安全に埋め込む）。 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** ポストの共有ページ URL（OGP 付き）。SNS へはこの URL を共有する。 */
function shareUrlOf(post: Post): string {
  return `${window.location.origin}/posts/${post.post_id}`;
}

/** SNS 共有で使うテキスト（本文＋投稿者・要件23-15）。 */
function shareTextOf(post: Post, lang: Lang): string {
  const base = post.body
    ? post.body
    : messages(lang).postPopup.authorSuffix(post.display_name);
  return base;
}

/**
 * 共有を実行する。Web Share API が使えれば navigator.share、
 * 使えなければ指定 SNS の Intent URL を新規タブで開く（要件23-14）。
 */
async function sharePost(
  post: Post,
  network: 'native' | 'x' | 'facebook' | 'linkedin',
  lang: Lang,
): Promise<void> {
  const url = shareUrlOf(post);
  const text = shareTextOf(post, lang);

  if (network === 'native' && typeof navigator.share === 'function') {
    try {
      // text と url を分けて渡すと受け側が url しか拾わないことがあるため、
      // 本文と URL をまとめた text を渡し、url は指定しない（本文の取りこぼし防止）。
      await navigator.share({
        title: messages(lang).postPopup.authorSuffix(post.display_name),
        text: `${text}\n${url}`,
      });
    } catch {
      // ユーザーがキャンセルした等は無視
    }
    return;
  }

  let intent: string;
  switch (network) {
    case 'x':
      intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      break;
    case 'facebook':
      intent = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
      break;
    case 'linkedin':
      intent = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
      break;
    default:
      // native 指定だが Web Share API 非対応の環境 → X にフォールバック
      intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  }
  window.open(intent, '_blank', 'noopener,noreferrer');
}

/** ポストが動画かどうか。 */
function isVideoPost(post: Post): boolean {
  return post.media_type === 'video' && !!post.video_url;
}

/**
 * ポストピンの DOM 要素を作る。
 * - 動画: サムネ（あれば）で塗り、▶ を重ねる。サムネ無しは🎬アイコン
 * - 画像: サムネイルで塗る
 * - テキストのみ: 💬アイコン
 */
function buildPostPinElement(post: Post): HTMLElement {
  const el = document.createElement('div');
  el.className = 'post-pin';
  if (isVideoPost(post)) {
    if (post.thumb_url) {
      el.classList.add('post-pin--image');
      el.style.backgroundImage = `url("${post.thumb_url}")`;
      // 再生マークを重ねる
      const play = document.createElement('span');
      play.className = 'post-pin__play';
      play.textContent = '▶';
      el.appendChild(play);
    } else {
      el.textContent = '🎬';
    }
  } else if (post.image_url) {
    // 画像投稿はサムネイルで塗る（絵文字は隠す）
    el.classList.add('post-pin--image');
    el.style.backgroundImage = `url("${post.image_url}")`;
  } else {
    // テキストのみの投稿はメッセージ（吹き出し）アイコン
    el.textContent = '💬';
  }
  return el;
}

/** クラスタ（複数ポストの束）のピン要素を作る。件数バッジ＋ポストらしい角丸スタイル。 */
function buildClusterElement(count: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'post-cluster';
  el.textContent = String(count);
  return el;
}

/** スポット一覧の1ページあたり件数（3列×2行）。 */
const SPOT_PAGE_SIZE = 6;

/** スポット一覧の1件（サムネ）の中身を作る。data-idx は全体 index を保つ。 */
function buildSpotItemHtml(p: Post, globalIdx: number): string {
  let inner: string;
  if (isVideoPost(p) && p.thumb_url) {
    inner = `<img src="${escapeHtml(p.thumb_url)}" alt="" /><span class="post-spot__play">▶</span>`;
  } else if (isVideoPost(p)) {
    inner = '<span class="post-spot__emoji">🎬</span>';
  } else if (p.image_url) {
    inner = `<img src="${escapeHtml(p.image_url)}" alt="" />`;
  } else {
    inner = '<span class="post-spot__emoji">💬</span>';
  }
  return `<button type="button" class="post-spot__item" data-idx="${globalIdx}">${inner}</button>`;
}

/**
 * スポット一覧（同一・極近接座標の複数ポスト）の吹き出し HTML を作る。
 * サムネのグリッドをページ送りで並べ、クリックで個別ポストの吹き出しへ切り替える（案2）。
 * @param page 0 始まりのページ番号
 */
function buildSpotListHtml(posts: Post[], lang: Lang, page: number): string {
  const pp = messages(lang).postPopup;
  const totalPages = Math.max(1, Math.ceil(posts.length / SPOT_PAGE_SIZE));
  const cur = Math.min(Math.max(0, page), totalPages - 1);
  const start = cur * SPOT_PAGE_SIZE;
  const slice = posts.slice(start, start + SPOT_PAGE_SIZE);
  const items = slice
    .map((p, i) => buildSpotItemHtml(p, start + i))
    .join('');

  // ページャ（2ページ以上のときだけ出す）。data-page に遷移先ページを持たせる。
  const pager =
    totalPages > 1
      ? `<div class="post-spot__pager">
<button type="button" class="post-spot__pager-btn" data-page="${cur - 1}" ${cur === 0 ? 'disabled' : ''}>${pp.prevPage}</button>
<span class="post-spot__pager-info">${pp.pageOf(cur + 1, totalPages)}</span>
<button type="button" class="post-spot__pager-btn" data-page="${cur + 1}" ${cur >= totalPages - 1 ? 'disabled' : ''}>${pp.nextPage}</button>
</div>`
      : '';

  return `<div class="post-spot">
<div class="post-spot__title">${pp.spotTitle(posts.length)}</div>
<div class="post-spot__grid">${items}</div>
${pager}
</div>`;
}

/** Web Share API が使える環境か（主にモバイル）。 */
function canUseWebShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * 共有ボタン群の HTML。
 *
 * 各 SNS へは Intent URL を使う（本文をクエリで確実に含めるため。要件23-15）。
 * Web Share API に本文＋URL を渡すと、受け側 SNS が URL しか拾わず本文が
 * 落ちることがある（Windows 共有経由の X 等）。そのため SNS ボタンは常に
 * Intent URL とし、Web Share API は「その他で共有」の補助ボタンに留める。
 */
function buildShareButtonsHtml(lang: Lang): string {
  const pp = messages(lang).postPopup;
  const snsButtons = `<button type="button" class="post-popup__share-btn" data-share="x" title="${pp.shareX}">𝕏</button>
<button type="button" class="post-popup__share-btn" data-share="facebook" title="${pp.shareFacebook}">f</button>
<button type="button" class="post-popup__share-btn" data-share="linkedin" title="${pp.shareLinkedin}">in</button>`;
  // Web Share API 対応環境（主にモバイル）では「その他」ボタンも足す。
  const nativeButton = canUseWebShare()
    ? `<button type="button" class="post-popup__share-btn" data-share="native" title="${pp.shareNative}">🔗</button>`
    : '';
  return `<div class="post-popup__share">${snsButtons}${nativeButton}</div>`;
}

/** クリック時に表示する吹き出しの HTML を作る。 */
function buildPopupHtml(
  post: Post,
  serverTime: number,
  isOwn: boolean,
  lang: Lang,
): string {
  const pp = messages(lang).postPopup;
  const parts: string[] = [];
  if (isVideoPost(post)) {
    // 動画はクリック再生（autoplay しない・転送量/UX配慮）。サムネを poster に。
    const poster = post.thumb_url
      ? ` poster="${escapeHtml(post.thumb_url)}"`
      : '';
    parts.push(
      `<video class="post-popup__video" src="${escapeHtml(post.video_url!)}" controls playsinline preload="none"${poster}></video>`,
    );
  } else if (post.image_url) {
    parts.push(
      `<img class="post-popup__img" src="${escapeHtml(post.image_url)}" alt="${escapeHtml(pp.imageAlt)}" loading="lazy" />`,
    );
  }
  parts.push('<div class="post-popup__meta">');
  const avatar = post.avatar_url
    ? `<img class="post-popup__avatar" src="${escapeHtml(post.avatar_url)}" alt="" />`
    : '';
  parts.push(
    `<div class="post-popup__author">${avatar}<span>${escapeHtml(post.display_name)}</span></div>`,
  );
  if (post.body) {
    parts.push(`<div class="post-popup__body">${escapeHtml(post.body)}</div>`);
  }
  parts.push(
    `<div class="post-popup__time">${relativeTime(post.created_at, serverTime, lang)}</div>`,
  );
  parts.push(buildShareButtonsHtml(lang));
  // 自分の投稿には削除ボタンを出す（本人のみ・要件23-8 の Web 削除）。
  if (isOwn) {
    parts.push(
      `<button type="button" class="post-popup__delete" data-delete="1">${escapeHtml(pp.delete)}</button>`,
    );
  }
  parts.push('</div>');
  return parts.join('');
}

/**
 * Popup の DOM に共有ボタン＋（自分の投稿なら）削除ボタンのハンドラを結線する。
 * @param onDelete 削除実行のコールバック（本人の投稿のときだけ呼ばれる）
 */
function wirePopupButtons(
  popup: Popup,
  post: Post,
  lang: Lang,
  onDelete?: (postId: string) => void,
): void {
  const el = popup.getElement();
  if (!el) {
    return;
  }
  const buttons = el.querySelectorAll<HTMLButtonElement>('.post-popup__share-btn');
  buttons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const network = btn.dataset.share as
        | 'native'
        | 'x'
        | 'facebook'
        | 'linkedin'
        | undefined;
      void sharePost(post, network ?? 'native', lang);
    });
  });

  const deleteBtn = el.querySelector<HTMLButtonElement>('.post-popup__delete');
  if (deleteBtn && onDelete) {
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (window.confirm(messages(lang).postPopup.confirmDelete)) {
        onDelete(post.post_id);
      }
    });
  }
}

/**
 * ポストピンの集合を管理するレイヤー。setPosts で差分更新する。
 */
/** supercluster の点プロパティ（ポスト本体を保持）。 */
type PostPointProps = { post: Post };
interface PostClusterProps {
  cluster: true;
  cluster_id: number;
  point_count: number;
}
function isPostCluster(
  p: PostPointProps | PostClusterProps,
): p is PostClusterProps {
  return (p as PostClusterProps).cluster === true;
}

/** 全世界 bbox（ビューポート制限なしで全ポストを対象にする）。 */
const WORLD_BBOX: [number, number, number, number] = [-180, -85, 180, 85];
/** クラスタリングの最大ズーム。これ以上は分離しない＝同一地点は一覧表示に回す。 */
const CLUSTER_MAX_ZOOM = 18;

/**
 * ポストピンの集合を管理するレイヤー（クラスタリング対応・要件23-19/20/21）。
 *
 * - 近接ポストは件数バッジのクラスタに束ねる（案1）。クラスタクリックで、
 *   まだ分離できるならズームイン、これ以上分離できない（同一座標）ならスポット一覧（案2）。
 * - 単独ポストは従来どおり個別ピン＋吹き出し。
 * - ズーム変化時のみ再クラスタ（pan では Marker が自動追従）。
 */
export class PostLayer {
  private readonly map: MaplibreMap;
  private markers = new Map<string, Marker>();
  private index: Supercluster<PostPointProps> | null = null;
  private serverTime = 0;
  private lastZoom: number | null = null;
  private readonly onZoom: () => void;
  /** ログイン中ユーザーの user_id（g_<sub>）。自分の投稿に削除ボタンを出す判定に使う。 */
  private currentUserId: string | null = null;
  /** 削除実行コールバック（App が認証付き API 呼び出しを担う）。 */
  private onDelete?: (postId: string) => void;
  /** 吹き出し・一覧の文言言語。 */
  private lang: Lang = 'ja';

  constructor(map: MaplibreMap) {
    this.map = map;
    this.onZoom = () => {
      const z = Math.round(this.map.getZoom());
      if (z !== this.lastZoom) {
        this.render();
      }
    };
    this.map.on('zoomend', this.onZoom);
  }

  /** 吹き出し文言の言語を設定する（lang 変更時に再描画するため setPosts も呼ぶ想定）。 */
  setLang(lang: Lang): void {
    this.lang = lang;
  }

  setContext(
    currentUserId: string | null,
    onDelete?: (postId: string) => void,
  ): void {
    this.currentUserId = currentUserId;
    this.onDelete = onDelete;
  }

  private isOwn(post: Post): boolean {
    return this.currentUserId !== null && post.user_id === this.currentUserId;
  }

  /** ポスト一覧を反映する。supercluster index を作り直して再描画する。 */
  setPosts(posts: Post[], serverTime: number): void {
    this.serverTime = serverTime;
    this.index = new Supercluster<PostPointProps>({
      radius: 50,
      maxZoom: CLUSTER_MAX_ZOOM,
    });
    this.index.load(
      posts.map((post) => ({
        type: 'Feature',
        properties: { post },
        geometry: { type: 'Point', coordinates: [post.lng, post.lat] },
      })),
    );
    this.lastZoom = null;
    this.render();
  }

  /** 個別ポストの吹き出しを持つ Marker を生成する。 */
  private buildPostMarker(post: Post, lng: number, lat: number): Marker {
    const own = this.isOwn(post);
    const el = buildPostPinElement(post);
    const popup = new Popup({
      offset: 20,
      className: 'post-popup',
      maxWidth: '260px',
    }).setHTML(buildPopupHtml(post, this.serverTime, own, this.lang));
    popup.on('open', () =>
      wirePopupButtons(popup, post, this.lang, own ? this.onDelete : undefined),
    );
    return new Marker({ element: el })
      .setLngLat([lng, lat])
      .setPopup(popup);
  }

  /**
   * クラスタをクリックしたときの挙動。
   * - まだ分離できる（展開ズーム > 現在ズーム）→ ズームイン（案1）
   * - これ以上分離できない（同一座標）→ スポット一覧の吹き出しを開く（案2）
   */
  private handleClusterClick(
    clusterId: number,
    lng: number,
    lat: number,
    marker: Marker,
  ): void {
    if (!this.index) {
      return;
    }
    const expansionZoom = this.index.getClusterExpansionZoom(clusterId);
    const currentZoom = Math.round(this.map.getZoom());
    if (expansionZoom > currentZoom && expansionZoom <= CLUSTER_MAX_ZOOM) {
      // まだ分離できる → ズームインして展開
      this.map.easeTo({ center: [lng, lat], zoom: expansionZoom });
      return;
    }
    // これ以上分離できない（同一・極近接座標）→ スポット一覧を提示
    const leaves = this.index.getLeaves(clusterId, Infinity) as unknown as Array<{
      properties: PostPointProps;
    }>;
    const spotPosts = leaves.map((l) => l.properties.post);
    const popup = new Popup({
      offset: 20,
      className: 'post-popup',
      maxWidth: '280px',
    }).setHTML(buildSpotListHtml(spotPosts, this.lang, 0));
    popup.on('open', () => this.wireSpotList(popup, spotPosts, lng, lat, 0));
    marker.setPopup(popup);
    marker.togglePopup();
  }

  /**
   * スポット一覧の結線。サムネクリックで個別ポストへ、ページャクリックでページ切替。
   * @param page 現在表示中のページ（0 始まり）
   */
  private wireSpotList(
    popup: Popup,
    spotPosts: Post[],
    lng: number,
    lat: number,
    page: number,
  ): void {
    const el = popup.getElement();
    if (!el) {
      return;
    }
    // サムネ → 個別ポスト詳細へ（data-idx は全体 index）
    el.querySelectorAll<HTMLButtonElement>('.post-spot__item').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.idx);
        const post = spotPosts[idx];
        if (!post) {
          return;
        }
        const own = this.isOwn(post);
        // 一覧の popup を個別 popup に差し替えて再表示する。戻り先ページも渡す。
        popup.setHTML(buildPopupHtml(post, this.serverTime, own, this.lang));
        const inner = popup.getElement();
        if (inner) {
          wirePopupButtons(
            popup,
            post,
            this.lang,
            own ? this.onDelete : undefined,
          );
          this.appendBackToList(popup, spotPosts, lng, lat, page);
        }
      });
    });
    // ページャ → 同じ一覧の別ページへ差し替えて再結線
    el.querySelectorAll<HTMLButtonElement>('.post-spot__pager-btn').forEach(
      (btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (btn.disabled) {
            return;
          }
          const next = Number(btn.dataset.page);
          popup.setHTML(buildSpotListHtml(spotPosts, this.lang, next));
          this.wireSpotList(popup, spotPosts, lng, lat, next);
        });
      },
    );
  }

  /**
   * 個別ポスト吹き出しに「一覧へ戻る」ボタンを足す。
   * @param page 戻り先の一覧ページ（詳細を開いたときのページを保つ）
   */
  private appendBackToList(
    popup: Popup,
    spotPosts: Post[],
    lng: number,
    lat: number,
    page: number,
  ): void {
    const el = popup.getElement();
    const content = el?.querySelector('.post-popup__meta') ?? el;
    if (!content) {
      return;
    }
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'post-spot__back';
    back.textContent = messages(this.lang).postPopup.backToList;
    back.addEventListener('click', (e) => {
      e.stopPropagation();
      popup.setHTML(buildSpotListHtml(spotPosts, this.lang, page));
      this.wireSpotList(popup, spotPosts, lng, lat, page);
    });
    content.appendChild(back);
  }

  /** 現在ズームでクラスタリングして再描画する。 */
  private render(zoomOverride?: number): void {
    if (!this.index) {
      return;
    }
    const zoom = zoomOverride ?? Math.round(this.map.getZoom());
    this.lastZoom = zoom;
    const clusters = this.index.getClusters(WORLD_BBOX, zoom);
    const nextIds = new Set<string>();

    for (const c of clusters) {
      const [lng, lat] = c.geometry.coordinates;
      const props = c.properties as PostPointProps | PostClusterProps;

      if (isPostCluster(props)) {
        const id = `post-cluster-${props.cluster_id}`;
        nextIds.add(id);
        // クラスタは件数のみ。内容は作り直す（クリック時の leaves 取得は最新 index を使う）。
        this.markers.get(id)?.remove();
        const el = buildClusterElement(props.point_count);
        const marker = new Marker({ element: el }).setLngLat([lng, lat]);
        const clusterId = props.cluster_id;
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleClusterClick(clusterId, lng, lat, marker);
        });
        marker.addTo(this.map);
        this.markers.set(id, marker);
      } else {
        const post = props.post;
        const id = `post-${post.post_id}`;
        nextIds.add(id);
        // 相対時刻等が変わるので作り直す。
        this.markers.get(id)?.remove();
        const marker = this.buildPostMarker(post, lng, lat).addTo(this.map);
        this.markers.set(id, marker);
      }
    }

    for (const [id, marker] of this.markers) {
      if (!nextIds.has(id)) {
        marker.remove();
        this.markers.delete(id);
      }
    }
  }

  /** 全 Marker を破棄する（アンマウント時）。 */
  destroy(): void {
    this.map.off('zoomend', this.onZoom);
    for (const marker of this.markers.values()) {
      marker.remove();
    }
    this.markers.clear();
  }
}
